import React, { useMemo } from 'react';
import { Box, Flex, Select, SelectProps } from '@chakra-ui/react';
import { IOption } from '../../../types';
import { withConfirm } from '../withConfirm';
import { Input } from '../Input';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

interface IProps extends DirectionProp {
  value?: string | number;
  id?: string;
  name?: string;
  otherValue?: string | number;
  placeHolder?: string;
  options?: IOption[];
  otherOptionLabel?: string;
  /**
   * Options that trigger the free-text input when selected. Appended to `options` before rendering.
   * If a value in `otherOptions` already exists in `options`, the duplicate is dropped —
   * the first occurrence (from `options`) is kept and the entry still qualifies as an "other" selection,
   * so the free-text input will appear as expected.
   */
  otherOptions?: IOption[];
  label?: string;
  subText?: string;
  inputHelperText?: string;
  errorText?: string;
  forwardedRef?: ((instance: HTMLSelectElement | null) => void) | React.RefObject<HTMLSelectElement> | null | undefined;
  onChange?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onChangeOtherText?: (event: React.FormEvent<HTMLInputElement>) => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  selectProps?: SelectProps;
}

const OTHER_VALUE_FALLBACK = 'other';

const SelectWithOther: React.FC<IProps> = ({
  value,
  id,
  name,
  otherValue,
  placeHolder = 'Please choose an option…',
  options,
  otherOptionLabel,
  otherOptions,
  label,
  subText,
  errorText,
  forwardedRef,
  onChange,
  onBlur,
  onFocus,
  onChangeOtherText,
  canUndo = false,
  onUndo,
  size = 'lg',
  mb = 0,
  direction,
  selectProps,
}) => {
  const optionsToRender = useMemo(() => {
    if (!options) {
      return [];
    }
    const includedOthers = [...options];
    if (otherOptions) {
      includedOthers.push(...otherOptions);
    }
    const seenOptionValues = new Set<string | number | undefined>();
    const optionsList = includedOthers.filter(option => {
      if (option.populate) {
        return true;
      }
      if (seenOptionValues.has(option.value)) {
        return false;
      }
      seenOptionValues.add(option.value);
      return true;
    });
    return optionsList;
  }, [options, otherOptions]);

  const renderOptions = optionsToRender.map((option, key) => {
    if (option.populate) {
      return (
        <optgroup label={option.label} key={key}>
          {option.populate.options.map((opt, index) => (
            <option data-selector="select-option" key={`${key}-${index}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </optgroup>
      );
    }
    return (
      <option data-selector="select-option" key={key} value={option.value}>
        {option.label}
      </option>
    );
  });

  const isOtherSelected = useMemo(
    () => value === OTHER_VALUE_FALLBACK || (otherOptions?.some(option => option.value === value) ?? false),
    [value, otherOptions],
  );

  const onSelect = React.useCallback(
    (event: React.FormEvent<HTMLSelectElement>) => {
      // ignore placeholder option
      if (onChange && event.currentTarget.value !== '') onChange(event);
    },
    [onChange],
  );

  return (
    <CustomFormControl
      id={id}
      controlProps={{
        isInvalid: !!(errorText && !isOtherSelected),
        mb,
      }}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ htmlFor: `${id}-input-field` }}
      subLabel={subText}
      error={errorText}
    >
      <Flex gap={{ base: 1, lg: 2 }}>
        <Box flexGrow="1">
          <Select
            id={`${id}-input-field`}
            name={name}
            data-selector={`${id}-input-field`}
            aria-label={label ? undefined : label}
            onChange={onSelect}
            value={value}
            ref={forwardedRef}
            isInvalid={isOtherSelected ? false : !!errorText}
            onBlur={onBlur}
            onFocus={onFocus}
            size={size}
            placeholder={placeHolder}
            {...selectProps}
          >
            {renderOptions}
          </Select>

          {isOtherSelected ? (
            <Box mt={6}>
              <Input
                id={`${id}_other`}
                name={`${name}_other`}
                label={otherOptionLabel}
                onChange={onChangeOtherText}
                errorText={errorText}
                value={otherValue}
              />
            </Box>
          ) : null}
        </Box>

        {canUndo ? <UndoEdit id={id} onUndo={onUndo} /> : null}
      </Flex>
    </CustomFormControl>
  );
};

const SelectWithOtherWithConfirm = withConfirm(SelectWithOther);

export { SelectWithOther, SelectWithOtherWithConfirm };
